/** Mobile app chrome: the pill menu button, round icon buttons, the page top bar and the floating tab bar. */
import type { ReactNode } from 'react';
import { createStore, useStore } from '../../lib/store';
import { Link, usePath } from '../../lib/router';
import { toggleTheme, useTheme } from '../../lib/theme';
import { useAccountKind, useMe } from '../../stores/session';
import { NotificationBell } from '../NotificationBell';
import { cx, Icon, type IconName } from '../ui';

// ---- Menu open state (the menu itself lives in MobileMenu) ----
const menuStore = createStore({ open: false });
export const openMenu = () => menuStore.set({ open: true });
export const closeMenu = () => menuStore.set({ open: false });
export const useMenuOpen = () => useStore(menuStore, (s) => s.open);

// ---- Last instrument viewed, so the Trade tab returns to it ----
const LAST_SYMBOL = 'lastSymbol';
export function lastSymbol(): string {
  try {
    return localStorage.getItem(LAST_SYMBOL) || 'BTC';
  } catch {
    return 'BTC';
  }
}
export function rememberSymbol(symbol: string) {
  try {
    localStorage.setItem(LAST_SYMBOL, symbol);
  } catch {
    /* storage unavailable */
  }
}

/** Floating surface: white with a soft shadow in light mode, raised grey with a hairline in dark mode. */
export const floatCls = 'border border-float-line bg-float text-fg shadow-float';
export const roundCls = cx('grid size-12 shrink-0 place-items-center rounded-full no-underline', floatCls);

/** "☰ Live account" — opens the app menu. */
export function MenuPill() {
  const me = useMe();
  const kind = useAccountKind();
  const label = me ? (kind === 'live' ? 'Live account' : 'Demo account') : 'Menu';
  return (
    <button
      type="button"
      onClick={openMenu}
      aria-haspopup="dialog"
      aria-label={me ? `Open menu, ${label}` : 'Open menu'}
      className={cx('inline-flex h-12 min-w-0 items-center gap-3 rounded-full pr-[22px] pl-[18px] text-[17px] font-semibold', floatCls)}
    >
      <Icon name="menu" size={22} strokeWidth={2.2} />
      <span className="truncate">{label}</span>
    </button>
  );
}

export function ThemeButton() {
  const theme = useTheme();
  return (
    <button type="button" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} className={roundCls}>
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={22} />
    </button>
  );
}

/** Notification bell as a round floating button (signed in only). */
export function BellButton() {
  const me = useMe();
  if (!me) return null;
  return <NotificationBell buttonClassName={cx(roundCls, '!size-12 !text-fg')} />;
}

/** Top of a mobile page: menu pill on the left, the bell and the theme button (or `right`) on the right. */
export function MobileTopBar({ right }: { right?: ReactNode }) {
  return (
    <header className="flex items-center justify-between gap-3 px-4 pt-[calc(env(safe-area-inset-top)+12px)]">
      <MenuPill />
      {right ?? (
        <div className="flex items-center gap-2">
          <BellButton />
          <ThemeButton />
        </div>
      )}
    </header>
  );
}

const TABS: { label: string; icon: IconName; to: () => string; match: (p: string) => boolean }[] = [
  { label: 'Portfolio', icon: 'portfolio', to: () => '/portfolio', match: (p) => p === '/portfolio' },
  { label: 'Markets', icon: 'bars', to: () => '/markets', match: (p) => p === '/markets' },
  { label: 'Trade', icon: 'candles', to: () => `/trade/${lastSymbol()}`, match: (p) => p === '/trade' || p.startsWith('/trade/') },
  { label: 'Wallet', icon: 'wallet', to: () => '/wallet', match: (p) => p === '/wallet' },
  { label: 'Account', icon: 'user', to: () => '/account', match: (p) => p === '/account' || p.startsWith('/account/') },
];

/** Floating glass tab bar pinned to the bottom of the screen. */
export function MobileTabBar() {
  const path = usePath();
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-4 bottom-[max(20px,env(safe-area-inset-bottom))] z-40 mx-auto grid h-[72px] max-w-[520px] grid-cols-5 gap-0.5 rounded-[36px] border border-float-line bg-glass p-1.5 shadow-[0_12px_32px_var(--shadow)] backdrop-blur-xl"
    >
      {TABS.map((t) => {
        const on = t.match(path);
        return (
          <Link
            key={t.label}
            to={t.to()}
            aria-current={on ? 'page' : undefined}
            className={cx('flex min-w-0 flex-col items-center justify-center gap-1 rounded-[30px] text-[12.5px] font-semibold text-fg no-underline', on && 'bg-float-on')}
          >
            <Icon name={t.icon} size={24} strokeWidth={t.icon === 'bars' ? 2.2 : 2} />
            <span className="truncate">{t.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** Small toggle switch used inside a `role="switch"` row. */
export function SwitchTrack({ on }: { on: boolean }) {
  return (
    <span aria-hidden="true" className={cx('relative h-8 w-[52px] shrink-0 rounded-full transition-colors', on ? 'bg-fg' : 'bg-line-strong')}>
      <span className={cx('absolute top-[3px] left-[3px] size-[26px] rounded-full shadow transition-transform', on ? 'translate-x-5 bg-bg' : 'bg-white')} />
    </span>
  );
}
